import { ChangeEvent, FC, useEffect, useState } from 'react';
import './style.scss';
import { validateInput } from '../../utils/validators/Validators';
import { FormControl, FormHelperText, Grid, IconButton, TextFieldProps, Typography, useTheme } from '@mui/material';
import { FormValidators } from '../Form';
import FileUpload from 'react-material-file-upload';
import { CloudDownloadOutlined } from '@mui/icons-material';
import { ToolTip } from '../Tooltip/ToolTip';
import strings from 'common/Translation/Translate';

interface UploadProps {
  inputValue?: any;
  field?: string;
  label?: string;
  typeValue?: any;
  textChange?: (value: string, field: string, error?: { error: boolean; message: string }, deleted?: any) => void;
  validators?: FormValidators[];
  hasError?: boolean;
  fieldError?: boolean | string;
  multiline?: boolean;
  accept?: string | string[];
  sx?: any;
}

const Upload: FC<UploadProps & TextFieldProps> = (
  {
    inputValue = [],
    field,
    label,
    validators,
    textChange,
    hasError,
    fieldError,
    multiline = false,
    accept,
    sx,
  }) => {
  const theme = useTheme();
  const [files, setFiles] = useState<File[]>(inputValue);
  const [error, setError] = useState<{ error: boolean; message: string } | null>(null);

  useEffect(() => {
    const uniqueFiles = inputValue?.filter(
      (file: any, index: number, self: any[]) =>
        index == self.findIndex((f) => f.name === file.name),
    );
    setFiles(uniqueFiles);
  }, [inputValue]);

  const handleChange = (value: any, event: ChangeEvent<HTMLInputElement>) => {
    if (typeof event !== 'undefined') {
      Array.from(event.target.files || (value as FileList)).forEach((file: any) =>
        Object.assign(file, {
          preview: URL.createObjectURL(file),
        }),
      );
    }
    const inputError = validateInput(validators, value);
    setError(inputError);

    if (textChange) {
      if (typeof event !== 'undefined') {
        if (multiline) {
          setFiles((files: any) => [...files, ...Array.from(value)]);
          textChange([...files, ...Array.from(value)] as any, field as string, inputError, event);
        } else {
          setFiles(value);
          textChange(value, field as string, inputError, event);
        }
      } else {
        let getIds = files.filter((object1: any) => {
          return !value.some((object2: any) => {
            return object1.id === object2.id;
          });
        });
        textChange(value, field as string, inputError, getIds || ([] as any));
      }
    }
  };


  const customErrorMessage = () => !error && hasError && fieldError ? strings.requiredText : '';

  return (
    <Grid container>
      <Grid item xs={12} sx={{ mt: 0, mb: 2 }}>
        {label && <Typography variant="subtitle1">{label}</Typography>}

        <FileUpload
          accept={accept}
          value={files as any}
          maxSize={10_000_000}
          multiple={multiline}
          sx={{
            border: '2px dashed', minHeight: '194px',
            '.MuiFormHelperText-root': {
              'color': 'red',
            },
            ...sx,
          }}
          onChange={handleChange as any}
          buttonText={strings.upload_button_text}
          title={strings.maxFileSizeErrorMessage}
          buttonProps={{ sx: { color: '#ffffff' } }}
        />

        <FormControl error={!!((error && error.error) || (!error && hasError && fieldError))}>
          <FormHelperText>
            {error?.error ? error.message : customErrorMessage()}
          </FormHelperText>
        </FormControl>
      </Grid>

      {files?.length > 0 && (
        <Grid item container spacing={1} sx={{ mx: 0 }}>
          {files.map((fileData: any, index: number) => (
            <ToolTip key={fileData.name || index} title={fileData.preview}>
              <Grid
                item
                xs={12}
                sx={{
                  pb: 1,
                  position: 'relative',
                  alignItems: 'center',
                  border: `1px dashed ${theme.palette.primary.main}`,
                  borderRadius: '4px',
                  boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.25)',
                }}>
                <IconButton
                  aria-label="download"
                  color={'primary'}
                  sx={{ position: 'absolute', bottom: 0, right: 10 }}
                >
                  <a
                    href={fileData.preview}
                    download={fileData.name}
                    target="_blank"
                    rel="noreferrer"
                    className="download"
                  >
                    <CloudDownloadOutlined sx={{ color: theme.palette.primary.main }} />
                  </a>
                </IconButton>
                <Typography
                  sx={{
                    m: 1,
                    fontSize: '12px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    width: '85%',
                  }}
                >
                  {fileData.name}
                </Typography>
              </Grid>
            </ToolTip>
          ))}
        </Grid>
      )}
    </Grid>
  );
};
export default Upload;
